import { memo, useState, useCallback, forwardRef, useEffect } from "react";
import SingleOTPInput from "./otp-single-input";

const OTPInputComponent = forwardRef((props, ref) => {
  const {
    length,
    isNumberInput,
    autoFocus,
    disabled,
    onChangeOTP,
    invalidOtp,
    ...rest
  } = props;

  const [activeInput, setActiveInput] = useState(0);
  const [otpValues, setOTPValues] = useState(Array(length).fill(""));

  // console.log("OTP Value: ", otpValues);

  useEffect(() => {
    if (invalidOtp) {
      setActiveInput(0);
      setOTPValues(Array(length).fill(""));
    }
  }, [invalidOtp]);

  const handleOtpChange = useCallback(
    (otp) => {
      onChangeOTP(otp.join(""));
    },
    [onChangeOTP]
  );

  const getRightValue = useCallback(
    (str) => {
      if (!isNumberInput || !str) return str;
      return Number(str) >= 0 ? str : "";
    },
    [isNumberInput]
  );

  const changeCodeAtFocus = useCallback(
    (str) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || "";
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  const focusInput = useCallback(
    (inputIndex) => {
      setActiveInput(Math.max(Math.min(length - 1, inputIndex), 0));
    },
    [length]
  );

  const focusPrevInput = useCallback(
    () => focusInput(activeInput - 1),
    [activeInput, focusInput]
  );

  const focusNextInput = useCallback(
    () => focusInput(activeInput + 1),
    [activeInput, focusInput]
  );

  const handleOnFocus = useCallback(
    (index) => () => focusInput(index),
    [focusInput]
  );

  const handleOnChange = useCallback(
    (e) => {
      const val = getRightValue(e.currentTarget.value);
      if (!val) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput, getRightValue]
  );

  const onBlur = useCallback(() => setActiveInput(-1), []);

  const handleOnKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "Backspace":
        case "Delete":
          e.preventDefault();
          changeCodeAtFocus("");
          if (!otpValues[activeInput]) focusPrevInput();
          break;
        case "ArrowLeft":
          e.preventDefault();
          focusPrevInput();
          break;
        case "ArrowRight":
          e.preventDefault();
          focusNextInput();
          break;
        default:
          if (/[^a-zA-Z0-9]/.test(e.key)) e.preventDefault();
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );

  const handleOnPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text/plain")
        .trim()
        .slice(0, length - activeInput)
        .split("");
      if (pastedData) {
        let nextFocusIndex = 0;
        const updatedOTPValues = [...otpValues];
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = getRightValue(pastedData.shift() || val);
            if (changedValue) {
              updatedOTPValues[index] = changedValue;
              nextFocusIndex = index;
            }
          }
        });
        setOTPValues(updatedOTPValues);
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
      }
    },
    [activeInput, getRightValue, length, otpValues]
  );

  return (
    <div className="flex justify-center items-center gap-2" {...rest}>
      {Array(length)
        .fill("")
        .map((_, index) => (
          <SingleOTPInput
            key={`SingleInput-${index}`}
            type={isNumberInput ? "tel" : "text"}
            focus={activeInput === index}
            value={otpValues[index]}
            autoFocus={autoFocus}
            onFocus={handleOnFocus(index)}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            onBlur={onBlur}
            onPaste={handleOnPaste}
            className="h-10 md:h-16 w-10 md:w-16 text-black text-2xl font-bold text-center border border-grey-200 rounded-lg md:rounded-xl focus:border-grey-200 focus:bg-grey-25 focus:outline-none focus:ring-0"
            disabled={disabled}
            ref={index === activeInput ? ref : null}
          />
        ))}
    </div>
  );
});

const OTPInput = memo(OTPInputComponent);
export default OTPInput;
